import { useRef } from 'react'
import { useSelector } from 'react-redux'

import { useHorizontalResize } from 'hooks/useHorizontalResize'
import { useTrackHeight } from 'hooks/useTrackHeight'
import { useTimelineItemBodyRect } from 'hooks/timeline'
import { editorSelectors } from 'store/features/editor/selectors'
import { secondsToPixels } from 'utils/timeline'
import { TRACK_ITEM_CLASS_NAME } from 'consts'
import * as Styled from '../styled'
import { IMediaItem, MediaStatus } from 'types/media'
import TrackItemWrapper from '../../TrackItemWrapper'
import { useVisualMediaItemResize } from '../useVisualMediaItemResize'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { TrackItemLoading } from '../TrackItemLoading'
import { ThumbnailContent } from '../TrackItemThumbnailContent'
import { useTimelineContext } from 'context/TimelineContext'
import { isBlobUrl } from 'utils'
import { COLORS } from 'consts/colors'
import { useMediaItemFrames } from '../../hooks/use-media-item-frames'
import { useLoadingProgressUpdate } from './useLoadingProgressUpdate'
import { isTextMediaItem } from 'utils'
interface ITrackItemProps {
  data: IMediaItem
  isActive: boolean
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
  updateMediaItems: (mediaItems: IMediaItem[]) => void
  isResizeBtnsVisible?: boolean
  selectedBorderColor: string
  selectedBorderSize: number
  /** Row DropLine height; defaults to useTrackHeight() when omitted. */
  trackRowHeight?: number
}

const TrackItem = ({
  data,
  isActive,
  onClick,
  updateMediaItems,
  isResizeBtnsVisible,
  selectedBorderColor,
  selectedBorderSize,
  trackRowHeight
}: ITrackItemProps) => {
  const { type, zIndex, id, duration, meta, startTime } = data

  const ref = useRef<HTMLDivElement>(null)
  const blobRef = useRef<string>('')

  const hasPendingStatus = meta.status === MediaStatus.PENDING
  const isBlobPath = Boolean(meta?.path && isBlobUrl(meta.path))

  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const defaultTrackHeight = useTrackHeight()
  const effectiveTrackHeight = trackRowHeight ?? defaultTrackHeight
  const speed = data.meta.playbackSpeed ?? 1
  const pixelsPerSecond = secondsToPixels(1, timelineRulerSliceDuration) / speed
  const frameSpacing = Math.max(
    1,
    Math.floor(
      (((meta.originalWidth ?? 0) / (meta.originalHeight ?? 0)) *
        effectiveTrackHeight) /
        speed /
        pixelsPerSecond
    )
  )

  const { scrollableContainerRef } = useTimelineContext()
  const { frames, framesByTime } = useMediaItemFrames(data)

  const { isItemGenerating, progress } = useLoadingProgressUpdate(
    id,
    hasPendingStatus
  )

  const isText = isTextMediaItem(type)
  const isItemLoading = isItemGenerating || (!meta.path && !isText)

  const { onResizeStart, onResizeEnd, handleLeftResize, handleRightResize } =
    useVisualMediaItemResize({
      itemId: id,
      lineIndex: zIndex,
      mediaItems,
      updateMediaItems
    })

  const { onLeftResize, onRightResize } = useHorizontalResize({
    ref,
    onResizeEnd,
    onResizeStart,
    handleLeftResize,
    handleRightResize,
    scrollableContainerRef,
    left: secondsToPixels(startTime, timelineRulerSliceDuration)
  })

  const { bodyWidth: videoThumbnailWidth, bodyLeft: videoThumbnailLeft } =
    useTimelineItemBodyRect({
      trimStart: meta.trimStart ?? 0,
      originalDuration: meta.originalDuration,
      playbackSpeed: speed
    })

  if (isBlobPath && meta.path) {
    blobRef.current = meta.path
  }

  return (
    <Styled.TrackItem
      onClick={onClick}
      ref={ref}
      id={data.id}
      className={TRACK_ITEM_CLASS_NAME}
      data-type={data.type}
      data-asset-id={data.assetId}
    >
      <TrackItemWrapper
        isActive={isActive}
        isDisabled={isItemLoading}
        onLeftResize={onLeftResize}
        onRightResize={onRightResize}
        isResizeBtnsVisible={isResizeBtnsVisible}
        selectedBorderColor={selectedBorderColor}
        selectedBorderSize={selectedBorderSize}
      >
        {isItemLoading ? (
          <TrackItemLoading
            mediaPath={meta.path}
            thumbnail={frames[0]}
            progress={progress}
            isItemGenerating={isItemGenerating}
          />
        ) : (
          <ThumbnailContent
            type={type}
            thumbnail={blobRef.current || frames[0]}
            videoThumbnailWidth={videoThumbnailWidth}
            videoThumbnailLeft={videoThumbnailLeft}
            framesByTime={framesByTime}
            frameSpacing={frameSpacing}
            pixelsPerSecond={pixelsPerSecond}
            trimStart={meta?.trimStart ?? 0}
            textData={meta?.textData}
          />
        )}
      </TrackItemWrapper>
      {isText ? null : (
        <Styled.Duration>{Math.floor(duration * 10) / 10}s</Styled.Duration>
      )}
      {isBlobPath && (
        <Styled.BlobLoader
          size={16}
          wrapperSize={20}
          loaderColor={COLORS.white}
        />
      )}
    </Styled.TrackItem>
  )
}

export default TrackItem
